/* Admin → Users page. Spykar Admin creates users, sets their role and scope
 * (outlets / locations / states / zones / distributor mappings). Non-admin
 * roles are constrained by scope on read (see backend routes_admin.py).
 */
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, RefreshCw, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin"]);

const ROLE_OPTIONS = [
    { value: "retailer", label: "Retailer / Outlet User" },
    { value: "distributor", label: "Distributor User" },
    { value: "asm", label: "ASM User" },
    { value: "rsm", label: "RSM User" },
    { value: "state_manager", label: "State / Zone / Management User" },
    { value: "spykar_admin", label: "Spykar Admin" },
    { value: "system_admin", label: "System / Support Admin" },
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

const emptyScope = () => ({
    outlets: [], locations: [], states: [], zones: [], distributor_ids: [],
    // Spykar master-linked scope dimensions (dynamic — matches master field keys)
    rsm: [], trim: [], territory: [], distributor: [], retailer: [],
    // Any admin-added custom master fields end up under `custom`.
    custom: {},
});
const emptyForm = () => ({
    id: null,
    email: "",
    name: "",
    role: "retailer",
    password: "",
    active: true,
    scope: emptyScope(),
});

export default function AdminUsers() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [showModal, setShowModal] = useState(false);
    const [masters, setMasters] = useState([]);  // Master field registry for scope dropdowns

    const load = async () => {
        setLoading(true);
        try {
            const [u, m] = await Promise.all([http.get("/admin/users"), http.get("/admin/masters")]);
            setRows(u.data);
            setMasters(m.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (user && ADMIN_ROLES.has(user.role)) load(); }, [user]);

    // Client-side role guard — non-admins get redirected home (backend also 403s).
    if (user && !ADMIN_ROLES.has(user.role)) {
        return <Navigate to="/" replace />;
    }

    const startCreate = () => { setForm(emptyForm()); setShowModal(true); };
    const startEdit = (u) => {
        setForm({
            id: u.id,
            email: u.email,
            name: u.name,
            role: u.role,
            password: "",
            active: u.active !== false,
            scope: { ...emptyScope(), ...(u.scope || {}) },
        });
        setShowModal(true);
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete user ${u.email}?`)) return;
        try {
            await http.delete(`/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-users-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="flex items-center gap-2 text-[20px] font-semibold">
                        <Users className="h-5 w-5" /> Users, Hierarchy & Access Control
                    </h1>
                    <p className="text-[12px] text-muted-foreground">
                        Assign roles and scope. Non-admin users only see data they are mapped to.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="admin-users-refresh"
                        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] hover:bg-muted/40"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                    <button
                        onClick={startCreate}
                        data-testid="admin-users-new"
                        className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90"
                    >
                        <UserPlus className="h-3.5 w-3.5" /> Add User
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-sm border border-border bg-card">
                <table className="w-full text-[12px]">
                    <thead className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Role</th>
                            <th className="px-3 py-2 text-left">Scope Summary</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => (
                            <tr key={u.id} className="border-t border-border hover:bg-muted/30" data-testid={`admin-user-row-${u.id}`}>
                                <td className="px-3 py-1.5 font-medium">{u.email}</td>
                                <td className="px-3 py-1.5">{u.name}</td>
                                <td className="px-3 py-1.5">
                                    <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-semibold">
                                        {ROLE_LABELS[u.role] || u.role}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 text-[11px] text-muted-foreground">
                                    <ScopeSummary scope={u.scope} />
                                </td>
                                <td className="px-3 py-1.5 text-[11px]">
                                    {u.active === false ? (
                                        <span className="text-red-500">Inactive</span>
                                    ) : (
                                        <span className="text-emerald-600">Active</span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                    <button
                                        onClick={() => startEdit(u)}
                                        data-testid={`admin-user-edit-${u.id}`}
                                        className="mr-1 inline-flex items-center gap-1 rounded-sm border border-border px-2 py-0.5 text-[11px] hover:bg-muted/40"
                                    >
                                        <Pencil className="h-3 w-3" /> Edit
                                    </button>
                                    <button
                                        onClick={() => remove(u)}
                                        data-testid={`admin-user-delete-${u.id}`}
                                        className="inline-flex items-center gap-1 rounded-sm border border-red-500/40 px-2 py-0.5 text-[11px] text-red-500 hover:bg-red-500/10"
                                    >
                                        <Trash2 className="h-3 w-3" /> Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                                    No users yet. Click Add User.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <UserModal
                    form={form}
                    setForm={setForm}
                    masters={masters}
                    onClose={() => setShowModal(false)}
                    onSaved={() => { setShowModal(false); load(); }}
                />
            )}
        </div>
    );
}

function ScopeSummary({ scope }) {
    const s = scope || {};
    const parts = [];
    if (s.rsm?.length) parts.push(`${s.rsm.length} RSM`);
    if (s.trim?.length) parts.push(`${s.trim.length} TRIM`);
    if (s.territory?.length) parts.push(`${s.territory.length} territory`);
    if (s.distributor?.length) parts.push(`${s.distributor.length} distributor`);
    if (s.retailer?.length) parts.push(`${s.retailer.length} retailer`);
    if (s.outlets?.length) parts.push(`${s.outlets.length} outlet(s)`);
    if (s.locations?.length) parts.push(`${s.locations.length} location(s)`);
    if (s.states?.length) parts.push(`${s.states.length} state(s)`);
    if (s.zones?.length) parts.push(`${s.zones.length} zone(s)`);
    if (s.custom && Object.keys(s.custom).length) {
        Object.entries(s.custom).forEach(([k, v]) => {
            if (v?.length) parts.push(`${v.length} ${k}`);
        });
    }
    return parts.length ? parts.join(" · ") : "—";
}

function UserModal({ form, setForm, masters, onClose, onSaved }) {
    const [saving, setSaving] = useState(false);
    const isEdit = !!form.id;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Master-driven scope setter — updates form.scope[masterKey] (or scope.custom[key] for non-standard masters)
    const STANDARD_SCOPE_KEYS = new Set(["rsm", "trim", "territory", "distributor", "retailer"]);
    const getScopeValues = (masterKey) => {
        if (STANDARD_SCOPE_KEYS.has(masterKey)) return form.scope[masterKey] || [];
        return (form.scope.custom && form.scope.custom[masterKey]) || [];
    };
    const setScopeValues = (masterKey, values) => {
        setForm((f) => {
            if (STANDARD_SCOPE_KEYS.has(masterKey)) {
                return { ...f, scope: { ...f.scope, [masterKey]: values } };
            }
            return { ...f, scope: { ...f.scope, custom: { ...(f.scope.custom || {}), [masterKey]: values } } };
        });
    };

    const save = async () => {
        if (!form.email.trim() || !form.name.trim()) return toast.error("Email and name are required");
        if (!isEdit && !form.password) return toast.error("Password is required for new users");
        setSaving(true);
        try {
            if (isEdit) {
                const payload = { name: form.name, role: form.role, scope: form.scope, active: form.active };
                if (form.password) payload.password = form.password;
                await http.patch(`/admin/users/${form.id}`, payload);
                toast.success("User updated");
            } else {
                await http.post("/admin/users", {
                    email: form.email,
                    name: form.name,
                    role: form.role,
                    password: form.password,
                    scope: form.scope,
                });
                toast.success("User created");
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-3xl overflow-hidden rounded-md border border-border bg-card shadow-lg" data-testid="admin-user-modal">
                <div className="border-b border-border px-5 py-3 text-[14px] font-semibold">
                    {isEdit ? "Edit User" : "Add User"}
                </div>
                <div className="max-h-[75vh] space-y-4 overflow-auto p-5">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Email *">
                            <input
                                type="email"
                                data-testid="admin-user-email"
                                value={form.email}
                                disabled={isEdit}
                                onChange={(e) => set("email", e.target.value)}
                                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px] disabled:opacity-60"
                            />
                        </Field>
                        <Field label="Name *">
                            <input
                                type="text"
                                data-testid="admin-user-name"
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                            />
                        </Field>
                        <Field label="Role *">
                            <select
                                data-testid="admin-user-role"
                                value={form.role}
                                onChange={(e) => set("role", e.target.value)}
                                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                            >
                                {ROLE_OPTIONS.map((r) => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label={isEdit ? "New password (leave blank to keep)" : "Password *"}>
                            <input
                                type="password"
                                data-testid="admin-user-password"
                                value={form.password}
                                onChange={(e) => set("password", e.target.value)}
                                placeholder={isEdit ? "•••••" : "min 6 chars"}
                                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                            />
                        </Field>
                    </div>

                    {isEdit && (
                        <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-border bg-muted/20 px-3 py-2">
                            <input
                                type="checkbox"
                                data-testid="admin-user-active"
                                checked={form.active}
                                onChange={(e) => set("active", e.target.checked)}
                                className="h-4 w-4 accent-emerald-600"
                            />
                            <span className="text-[12px] font-medium">
                                Active {form.active ? "" : "— user cannot log in and existing sessions are revoked"}
                            </span>
                        </label>
                    )}

                    {/* -------- Scope from Master Data -------- */}
                    <div className="rounded-sm border border-border bg-muted/20 p-3" data-testid="admin-user-scope-masters">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Scope · pick from master fields
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                                {masters.length} master field(s) available
                            </div>
                        </div>
                        {masters.length === 0 ? (
                            <div className="rounded-sm border border-dashed border-border p-3 text-center text-[11px] text-muted-foreground">
                                No master fields yet. Go to <em>Admin → Master Data</em> to add fields.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {masters.map((m) => (
                                    <MasterMultiSelect
                                        key={m.key}
                                        master={m}
                                        selected={getScopeValues(m.key)}
                                        onChange={(vals) => setScopeValues(m.key, vals)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
                    <button
                        onClick={onClose}
                        data-testid="admin-user-cancel"
                        className="rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-muted/40"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="admin-user-save"
                        className="rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {saving ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Multi-select dropdown driven by a master field's values. Uses a searchable
// popover with checkboxes so users with 800+ options (RSM, TRIM, Retailer)
// can still find and pick values quickly.
function MasterMultiSelect({ master, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [pos, setPos] = useState(null); // popover coords for fixed positioning
    const btnRef = useRef(null);
    const values = master.values || [];
    const s = new Set(selected);
    const filtered = search
        ? values.filter((v) => v.toLowerCase().includes(search.toLowerCase()))
        : values;

    const toggleOpen = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            // Flip upward if the popover would spill below the viewport
            const popH = 320;
            const below = r.bottom + 4;
            const spillsBelow = below + popH > window.innerHeight;
            setPos({
                left: r.left,
                width: r.width,
                top: spillsBelow ? r.top - popH - 4 : below,
            });
        }
        setOpen((o) => !o);
    };

    // Recompute on scroll/resize while open
    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            const popH = 320;
            const spillsBelow = r.bottom + 4 + popH > window.innerHeight;
            setPos({
                left: r.left,
                width: r.width,
                top: spillsBelow ? r.top - popH - 4 : r.bottom + 4,
            });
        };
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open]);

    const toggle = (v) => {
        const next = s.has(v) ? selected.filter((x) => x !== v) : [...selected, v];
        onChange(next);
    };
    const clearAll = () => onChange([]);

    return (
        <div data-testid={`admin-user-scope-master-${master.key}`}>
            <div className="mb-1 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {master.label}
                </div>
                <div className="text-[10px] text-muted-foreground">
                    {selected.length} / {values.length} selected
                    {selected.length > 0 && (
                        <button
                            type="button"
                            onClick={clearAll}
                            className="ml-2 text-red-500 hover:underline"
                            data-testid={`admin-user-scope-clear-${master.key}`}
                        >
                            clear
                        </button>
                    )}
                </div>
            </div>
            <button
                ref={btnRef}
                onClick={toggleOpen}
                type="button"
                data-testid={`admin-user-scope-toggle-${master.key}`}
                className="flex w-full items-center justify-between rounded-sm border border-border bg-background px-2 py-1.5 text-left text-[12px] hover:border-primary/60"
            >
                <span className="truncate">
                    {selected.length === 0
                        ? <span className="text-muted-foreground">Select {master.label}…</span>
                        : selected.length <= 3
                            ? selected.join(", ")
                            : `${selected.slice(0, 2).join(", ")} +${selected.length - 2} more`}
                </span>
                <span className="ml-2 text-muted-foreground">{open ? "▲" : "▼"}</span>
            </button>
            {open && pos && (
                <>
                    {/* Backdrop closes popover on outside click */}
                    <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
                    <div
                        className="fixed z-[61] rounded-sm border border-border bg-card shadow-lg"
                        style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: 320 }}
                    >
                        <div className="border-b border-border p-2">
                            <input
                                type="text"
                                placeholder="Search…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid={`admin-user-scope-search-${master.key}`}
                                className="w-full rounded-sm border border-border bg-background px-2 py-1 text-[11px] focus:border-primary/60 focus:outline-none"
                            />
                        </div>
                        <div className="max-h-[220px] overflow-auto">
                            {filtered.slice(0, 200).map((v) => (
                                <label
                                    key={v}
                                    className="flex cursor-pointer items-center gap-2 border-b border-border/50 px-2 py-1 text-[11px] hover:bg-muted/30"
                                >
                                    <input
                                        type="checkbox"
                                        checked={s.has(v)}
                                        onChange={() => toggle(v)}
                                        className="h-3.5 w-3.5 accent-primary"
                                    />
                                    <span className="truncate">{v}</span>
                                </label>
                            ))}
                            {filtered.length === 0 && (
                                <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">No matches</div>
                            )}
                            {filtered.length > 200 && (
                                <div className="px-2 py-2 text-center text-[10px] text-muted-foreground">
                                    +{filtered.length - 200} more · refine search
                                </div>
                            )}
                        </div>
                        <div className="border-t border-border px-2 py-1.5 text-right">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="text-[11px] font-semibold text-primary hover:underline"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            {children}
        </label>
    );
}
